import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

/**
 * WHICH TEXTS DO NOT FIT THEIR BOX, so only those carry a help tag (wave 5 round 5, C6-3,
 * for the column heads; R6 A9 / S2 for the cells). A tag on a text that is fully readable is
 * noise - this repository's own Tooltip contract calls a help tag "a label for an
 * already-labelled icon button" - so the box has to be asked, and the question is the one
 * the measure step asks about any clipped text: is there more content in the box than the
 * box is wide (`packages/e2e/src/measure.ts`, rule R2).
 *
 * The boxes are the descendants of `container` that carry `attribute`; its value is the
 * key the answer is filed under. The answer maps each clipped key to the text the box
 * shows, which is what its help tag says in full.
 *
 * ONE OBSERVER FOR THE WHOLE ROW rather than one per box: a table may carry forty columns,
 * and forty `ResizeObserver`s on forty static boxes is forty times the work for one answer.
 * The reading cannot loop, because wrapping a text in `Tooltip` adds no box - Radix's
 * trigger renders `asChild` onto the span it is given.
 */
export function useClippedText(
  container: RefObject<HTMLElement | null>,
  attribute: string,
  enabled: boolean,
): ReadonlyMap<string, string> {
  const [clipped, setClipped] = useState<ReadonlyMap<string, string>>(NONE);
  /**
   * THE LAST ANSWER, COMPARED BEFORE `setClipped` IS CALLED, NOT INSIDE IT (BR-DEPTH,
   * 2026-10-05). An updater that returns the previous map does not stop a render: React skips
   * an unchanged update without rendering only while the component has no other update in
   * flight, and right after a render the row's own update caused (its record landing in the
   * cache) it cannot, so it queues one more render. On the Agencies list in WebKit under load,
   * fifty rows re-rendered one task after another, each left that render queued, and React
   * counted the chain to its limit and threw error #185, "Maximum update depth exceeded".
   * Calling `setClipped` only when the answer differs leaves nothing queued.
   */
  const answer = useRef<ReadonlyMap<string, string>>(NONE);
  const read = useCallback(() => {
    const element = container.current;
    if (!enabled || element === null) return;
    const cut = new Map<string, string>();
    for (const box of element.querySelectorAll<HTMLElement>(`[${attribute}]`)) {
      if (box.scrollWidth > box.clientWidth + 1) {
        cut.set(box.getAttribute(attribute) ?? '', visibleText(box));
      }
    }
    if (sameEntries(answer.current, cut)) return;
    answer.current = cut;
    setClipped(cut);
  }, [container, attribute, enabled]);
  /* AFTER EVERY RENDER, WHICH IS WHEN THE COLUMNS OR THE VALUES CAN HAVE CHANGED. The widths
     are the colgroup's and do not move with the window, so there is no dependency list that
     says "the text is different now" - a field renamed or a value edited keeps the table
     exactly as wide as it was. The reading is a handful of integer properties over the boxes
     of one row, and the comparison with the last answer above is what stops the second pass. */
  useEffect(read);
  useEffect(() => {
    const element = container.current;
    if (!enabled || element === null) return;
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, [container, enabled, read]);
  return clipped;
}

const NONE: ReadonlyMap<string, string> = new Map();

function sameEntries(a: ReadonlyMap<string, string>, b: ReadonlyMap<string, string>): boolean {
  if (a.size !== b.size) return false;
  for (const [key, value] of a) {
    if (b.get(key) !== value) return false;
  }
  return true;
}

/**
 * THE TEXT A SIGHTED READER SEES in the box, which is what was cut off: a field display
 * also carries `sr-only` words for the screen reader ("late" beside an overdue date), and
 * those are not part of the value on the screen.
 */
function visibleText(box: HTMLElement): string {
  const parts: string[] = [];
  const walker = box.ownerDocument.createTreeWalker(box, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    if (node.parentElement?.closest('.sr-only') == null) parts.push(node.textContent ?? '');
  }
  return parts.join('').replace(/\s+/g, ' ').trim();
}
